import { useContext, useEffect } from "react";

import { MktgDataContext } from "@swa-ui/application";
import { useLocation } from "@swa-ui/browser";
import { swaDate } from "@swa-ui/date";
import { PlacementsContext } from "@swa-ui/placements";

import { getTripType } from "../utils";
import {
  getBookingCurveDetails,
  getFlightDetails,
  getSeatMapDetails,
  getTimeToDeparture,
} from "./airSeatsHelperUtils";
import { AIR_SEATS_ROUTES } from "./defines/airSeatRoutes";

export function useSelectSeatsAnalytics(
  airReservation,
  airSeat,
  passengerSelection,
  chaseVisaRrEnrolled,
  tripType,
  rrChaseCardType
) {
  const { bounds = [] } = airReservation ?? {};
  const { getAppId, flightSegments = [], getFlowType, seats, selectedSegment } = airSeat;
  const { activePassenger, passengers } = passengerSelection;
  const { placements = {} } = useContext(PlacementsContext) ?? {};
  const { setMktgPageData } = useContext(MktgDataContext);
  const { pathname } = useLocation();
  const flightSegment = flightSegments?.find(
    (flight, index) => index === selectedSegment
  )?.flightSegment;
  const paxNumber = passengers?.findIndex((passenger) => passenger?.id === activePassenger?.id) + 1;

  useEffect(() => {
    if (pathname === AIR_SEATS_ROUTES.select) {
      setMktgPageData(
        {
          air_triptype: getTripType(tripType),
          number_passengers: `${passengers?.length}`,
          pax_number: `${paxNumber}`,
          rrchasecardholder: chaseVisaRrEnrolled ? "1" : "0",
          rrchasecardtype: rrChaseCardType ?? "",
          segment_number: `${selectedSegment + 1}`,
          segments: `${flightSegments?.length}`,
          selectedfaretype: `${flightSegment?.fareFamily}`,
          standardseatsdisabled: flightSegment?.fareFamily === "WGA" ? "1" : "0",
          ...({ UPGRADE: { seatsupgrade: "1" }, CHANGE: { seatschange: "1" } }[getFlowType?.()] ||
            {}),
          ...(getAppId?.() === "air-booking" && { seatsinpath: "1" }),
          ...getFlightDetails(flightSegments, selectedSegment),
          ...getInformationalMessageData(),
          ...getSeatMapDetails(seats, selectedSegment),
          ...getStandaloneDetails(),
        },
        "selectSeats"
      );
    }
  }, [
    activePassenger,
    chaseVisaRrEnrolled,
    pathname,
    placements,
    seats,
    selectedSegment,
    rrChaseCardType,
  ]);

  const isSeatAssigned = () =>
    Object.values(seats).some(
      ({ originalSeats }) =>
        Array.isArray(originalSeats) &&
        originalSeats.some(
          (seat) =>
            seat?.segmentId === `${selectedSegment + 1}` &&
            seat?.passengerId === activePassenger?.id
        )
    );

  function getStandaloneDetails() {
    const standaloneData = {
      timetodeparture: "",
    };

    if (airReservation?.id) {
      const boundNumber = bounds?.findIndex((bound) =>
        bound?.segments?.some(
          (segment) =>
            segment?.departure?.airport === flightSegment?.origin &&
            segment?.arrival?.airport === flightSegment?.destination
        )
      );
      const filteredSegment = bounds
        ?.flatMap((bound) => bound?.segments)
        .find((seg) => seg?.id === `${selectedSegment + 1}`);
      const hasChildSSR = passengers?.some((passenger) =>
        passenger.specialServiceRequests?.some((ssr) => ssr === "CHILD")
      );
      const hasExtraSeat =
        activePassenger?.type === "EXTRA_SEAT" ||
        activePassenger?.extraSeatPassengerReferences?.length > 0;
      const timetoDepart = swaDate(filteredSegment?.departure?.scheduledAt).diff(
        swaDate(),
        "minutes"
      );
      const departWithInTwoDays = timetoDepart <= 2880 ? true : false;

      standaloneData["air_confirmationnumber"] = airReservation.id;
      standaloneData["bookingcurve"] = getBookingCurveDetails(
        filteredSegment?.departure?.scheduledAt
      );
      standaloneData["bound_number"] = boundNumber >= 0 ? `${boundNumber + 1}` : "";
      standaloneData["extraseat"] = hasExtraSeat ? "1" : "";
      standaloneData["familybooking"] = hasChildSSR ? "1" : "0";
      standaloneData["revenuestream"] = "seats";
      standaloneData["seatsstandalone"] = "1";
      standaloneData["seatsassigned"] = isSeatAssigned() ? "1" : "0";

      if (timetoDepart > 0 && departWithInTwoDays) {
        standaloneData["timetodeparture"] = getTimeToDeparture(
          filteredSegment?.departure?.scheduledAt
        );
      }
    }

    return standaloneData;
  }

  function getInformationalMessageData() {
    const informationalData = {};
    const placementId = Object.keys(placements).find((id) => id.includes("topMessageBlock03"));

    if (placementId && placements[placementId]?.id) {
      informationalData["informational_message_display"] = "1";
      informationalData["informational_message"] = placements[placementId].id;
    }

    return informationalData;
  }
}
